import React, {
  Dispatch, Ref, SetStateAction, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View, StyleSheet, Text, Pressable, ScrollView, TextInput, FlatList,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import SelectionModal from '../../../components/SelectionModal';
import {
  Area, Floor, Region, Space,
} from '../../../components/Map/SetFloorsModal/FloorItem';
import RegionDropdown from '../../../components/AssetManagement/AddAssetModal/components/RegionDropdown';
import getFloorData from '../../../requests/Locations/Floors/GetFloorData';
import getAreas from '../../../requests/Locations/Areas/GetAreas';
import getSpaces from '../../../requests/Locations/Spaces/GetSpaces';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import SearchIcon from '../../../components/SearchIcon';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import DeleteIcon24 from '../../../components/24pxIcons/DeleteIcon24';
import { updateBuildingId, updateBuildingName, updateSlaHours } from '../../../redux/selectedBuildingSlice';
import { Building } from '../../LocationManagement/components/AddBuildingModal';

interface LocationSelectionModalProps {
  isVisible: boolean,
  hide: () => void,
  buildingSelectionOnly?: boolean,
  setLocationId?: Dispatch<SetStateAction<string>>,
  setLocationName?: Dispatch<SetStateAction<string>>,
  setLocationCapacity?: Dispatch<SetStateAction<number>>,
  setLocationExternalId?: Dispatch<SetStateAction<string>>,
  setSelectedFloorId?: Dispatch<SetStateAction<string>>,
  setSelectedAreaId?: Dispatch<SetStateAction<string>>,
  setSelectedSpaceId?: Dispatch<SetStateAction<string>>,
}

const LocationSelectionModal = ({
  isVisible, hide, setLocationName, setLocationId, setLocationCapacity, setLocationExternalId,
  setSelectedFloorId, setSelectedAreaId, setSelectedSpaceId, buildingSelectionOnly,
}: LocationSelectionModalProps) => {
  const dispatch = useDispatch();
  const { id: selectedBuildingId } = useSelector((state:
    { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding);
  const buildings: object | null | undefined = useSelector((state:
    { locations }) => state.locations.buildings);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [allAreas, setAllAreas] = useState<Area[]>([]);
  const [allSpaces, setAllSpaces] = useState<Space[]>([]);
  const [selectedBuilding,
    setSelectedBuilding] = useState<Building>(buildings ? buildings[selectedBuildingId] : null);
  const [selectedFloor, setSelectedFloor] = useState<Floor>(null);
  const [selectedArea, setSelectedArea] = useState<Area>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space>(null);
  const [floorDropdownVisible, setFloorDropdownVisible] = useState<boolean>(false);
  const [areaDropdownVisible, setAreaDropdownVisible] = useState<boolean>(false);
  const [spaceDropdownVisible, setSpaceDropdownVisible] = useState<boolean>(false);
  const [searchedFloorsVisible, setSearchedFloorsVisible] = useState<boolean>(true);
  const [searchedAreasVisible, setSearchedAreasVisible] = useState<boolean>(true);
  const [searchedSpacesVisible, setSearchedSpacesVisible] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>('');
  const scrollViewRef: Ref<ScrollView | null> = useRef<ScrollView | null>(null);

  useEffect(() => {
    if (!selectedBuildingId) return;
    setSelectedFloor(null);
    setSelectedArea(null);
    setSelectedSpace(null);
    if (setLocationName) setLocationName(null);
    if (setLocationId) setLocationId(null);
    if (setLocationCapacity) setLocationCapacity(null);
    if (setLocationExternalId) setLocationExternalId(null);
    const updatedAllAreas: Area[] = [];
    const updatedAllSpaces: Space[] = [];
    getFloorData(selectedBuildingId).then((floorData: { locations: Floor[] }) => {
      const floorList: Floor[] = floorData?.locations ?? [];
      if (!floorList.length) return;
      setFloors(floorList?.sort((prev: Floor, next: Floor) => prev.Index - next.Index));
      floorList.forEach((floor: Floor) => {
        getAreas(floor.LocationId).then((areaData: { locations: Area[] }) => {
          const areasInFloor: Area[] = (areaData?.locations ?? [])
            .map((area: Area) => ({ ...area, FloorName: floor.Name?.trim() }));
          if (!areasInFloor.length) return;
          floor.areas = areasInFloor
            .sort((prev: Area, next: Area) => prev.Name.localeCompare(next.Name));
          updatedAllAreas.push(...areasInFloor);
          setAllAreas(updatedAllAreas
            .sort((prev: Area, next: Area) => prev.Name.localeCompare(next.Name)));
          areasInFloor.forEach((area: Area) => {
            getSpaces(area.LocationId).then((spaceData: { locations: Space[] }) => {
              const spacesInArea: Space[] = (spaceData?.locations ?? [])
                .map((space: Space) => ({
                  ...space, FloorName: floor.Name?.trim(), AreaName: area.Name?.trim(),
                }));
              if (!spacesInArea.length) return;
              updatedAllSpaces.push(...spacesInArea);
              setAllSpaces(updatedAllSpaces
                .sort((prev: Space, next: Space) => prev.Name.localeCompare(next.Name)));
              area.spaces = spacesInArea
                .sort((prev: Space, next: Space) => prev.Name.localeCompare(next.Name));
            });
          });
        });
      });
    });
  }, [selectedBuildingId]);

  useEffect(() => {
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
    if (setSelectedFloorId) setSelectedFloorId(selectedFloor?.LocationId);
    if (setSelectedAreaId) setSelectedAreaId(selectedFloor?.LocationId);
    if (setSelectedSpaceId) setSelectedSpaceId(selectedFloor?.LocationId);
    setSearchValue('');
  }, [selectedFloor, selectedArea, selectedSpace]);

  useEffect(() => {
    if (!floorDropdownVisible) return;
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
    scrollViewRef?.current.scrollToEnd();
  }, [floorDropdownVisible]);

  useEffect(() => {
    if (!areaDropdownVisible) return;
    setFloorDropdownVisible(false);
    setSpaceDropdownVisible(false);
    scrollViewRef?.current?.scrollToEnd();
  }, [areaDropdownVisible]);

  useEffect(() => {
    if (!spaceDropdownVisible) return;
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    scrollViewRef?.current?.scrollToEnd();
  }, [spaceDropdownVisible]);

  const filteredFloors: Floor[] = floors?.filter((floor: Floor) => floor.Name.toLowerCase()
    .includes(searchValue?.trim().toLowerCase()));

  const filteredAreas: Area[] = allAreas?.filter((area: Area) => area.Name.toLowerCase()
    .includes(searchValue?.trim().toLowerCase()));

  const filteredSpaces: Space[] = allSpaces?.filter((space: Space) => space.Name.toLowerCase()
    .includes(searchValue?.trim().toLowerCase()));

  const apply: () => void = () => {
    if (buildingSelectionOnly) {
      dispatch(updateBuildingId(selectedBuilding.LocationId));
      dispatch(updateBuildingName(selectedBuilding.Name));
      dispatch(updateSlaHours((selectedBuilding.SlaHours)));
      setFloorDropdownVisible(false);
      hide();
      return;
    }
    const selectedLocation: Region = selectedSpace ?? selectedArea ?? selectedFloor;
    const locationName: string = selectedLocation?.Name;
    let locationAddress: string = '';
    if (selectedSpace) locationAddress = `${selectedSpace.FloorName}, ${selectedSpace.AreaName}, `;
    else if (selectedArea) locationAddress = `${selectedArea.FloorName}, `;
    if (setLocationName) setLocationName(locationName ? `${locationAddress}${locationName}` : null);
    if (setLocationId) setLocationId(selectedLocation?.LocationId);
    if (setLocationCapacity) {
      setLocationCapacity(selectedSpace?.LocationCapacity ?? selectedArea?.LocationCapacity);
    }
    if (setLocationExternalId) setLocationExternalId(selectedLocation?.ExternalId);
    if (setSelectedFloorId) setSelectedFloorId(selectedFloor?.LocationId);
    if (setSelectedAreaId) setSelectedAreaId(selectedArea?.LocationId);
    if (setSelectedSpaceId) setSelectedSpaceId(selectedSpace?.LocationId);
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
    setSearchedAreasVisible(true);
    setSearchedFloorsVisible(true);
    setSearchedSpacesVisible(true);
    hide();
  };

  return <SelectionModal
    title='Location'
    {...{ scrollViewRef }}
    style={styles.container}
    listContent={<View style={styles.content}>
      {!buildingSelectionOnly && <View style={styles.searchBar}>
        <SearchIcon style={styles.searchIcon} />
        <TextInput
          style={styles.searchText}
          placeholder='Search'
          placeholderTextColor={Colors.light.hereworksBlack400}
          value={searchValue}
          onChangeText={setSearchValue}
        />
      </View>}
      <View style={{ zIndex: 2 }}>
        {searchValue?.trim()?.length > 0 && <ScrollView
          style={styles.searchResults}
          contentContainerStyle={{ backgroundColor: '#FFF' }}
        >
          <FlatList
            data={filteredFloors}
            scrollEnabled={false}
            ListHeaderComponent={<Pressable
              style={styles.listHeader}
              onPress={() => setSearchedFloorsVisible(!searchedFloorsVisible)}
            >
              <Text style={styles.listHeaderText}>{'Floors'}</Text>
              {filteredFloors?.length > 0 && <FontAwesome
                name='angle-down'
                color={Colors.light.hereworksBlack400}
                size={24}
                style={searchedFloorsVisible ? Style.inverted : Style.upright}
              />}
            </Pressable>}
            renderItem={({ item: floor }) => searchedFloorsVisible && <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResultItem,
                (hovered || pressed) && Style.listItemHovered,
              ]}
              onPress={() => {
                setSelectedFloor(floor);
                setSelectedArea(null);
                setSelectedSpace(null);
              }}
            >
              <Text style={styles.name}>{floor.Name}</Text>
            </Pressable>}
            ListEmptyComponent={<Text style={[styles.searchResultItem, styles.name]}>
              {'No floors found'}
            </Text>}
            keyExtractor={(floor: Floor) => `floor_${floor.LocationId}`}
          />
          <FlatList
            data={filteredAreas}
            scrollEnabled={false}
            ListHeaderComponent={<Pressable
              style={styles.listHeader}
              onPress={() => setSearchedAreasVisible(!searchedAreasVisible)}
            >
              <Text style={styles.listHeaderText}>{'Areas'}</Text>
              {filteredAreas?.length > 0 && <FontAwesome
                name='angle-down'
                color={Colors.light.hereworksBlack400}
                size={24}
                style={searchedAreasVisible ? Style.inverted : Style.upright}
              />}
            </Pressable>}
            renderItem={({ item: area }) => searchedAreasVisible && <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResultItem,
                (hovered || pressed) && Style.listItemHovered,
              ]}
              onPress={() => {
                setSelectedFloor(floors
                  .filter((floor: Floor) => floor.LocationId === area.ParentId)[0]);
                setSelectedArea(area);
                setSelectedSpace(null);
              }}
            >
              <Text style={styles.name}>{area.Name}</Text>
              <Text style={styles.address}>{area.FloorName}</Text>
            </Pressable>}
            ListEmptyComponent={<Text style={[styles.searchResultItem, styles.name]}>
              {'No areas found'}
            </Text>}
            keyExtractor={(area: Area) => `area_${area.LocationId}`}
          />
          <FlatList
            data={filteredSpaces}
            scrollEnabled={false}
            ListHeaderComponent={<Pressable
              style={styles.listHeader}
              onPress={() => setSearchedSpacesVisible(!searchedSpacesVisible)}
            >
              <Text style={styles.listHeaderText}>{'Spaces'}</Text>
              {filteredSpaces?.length > 0 && <FontAwesome
                name='angle-down'
                color={Colors.light.hereworksBlack400}
                size={24}
                style={searchedSpacesVisible ? Style.inverted : Style.upright}
              />}
            </Pressable>}
            renderItem={({ item: space }) => searchedSpacesVisible && <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResultItem,
                (hovered || pressed) && Style.listItemHovered,
              ]}
              onPress={() => {
                const parentArea: Area = allAreas
                  .filter((area: Area) => area.LocationId === space.ParentId)[0];
                setSelectedFloor(floors
                  .filter((floor: Floor) => floor.LocationId === parentArea.ParentId)[0]);
                setSelectedArea(parentArea);
                setSelectedSpace(space);
              }}
            >
              <Text style={styles.name}>{space.Name}</Text>
              <Text style={styles.address}>{`${space.FloorName}, ${space.AreaName}`}</Text>
            </Pressable>}
            ListEmptyComponent={<Text style={[styles.searchResultItem, styles.name]}>
              {'No spaces found'}
            </Text>}
            keyExtractor={(space: Space) => `space_${space.LocationId}`}
          />
        </ScrollView>}
      </View>
      <View style={styles.sectionHeader}>
        <Text style={styles.label}>
          {buildingSelectionOnly ? 'Building' : '1. Floor'}
        </Text>
        {selectedFloor && !buildingSelectionOnly && <PressableStyled onPress={() => {
          setSelectedFloor(null);
          setSelectedArea(null);
          setSelectedSpace(null);
        }}>
          <DeleteIcon24 />
        </PressableStyled>}
      </View>
      <Pressable
        style={styles.button}
        onPress={() => setFloorDropdownVisible(!floorDropdownVisible)}
      >
        <Text style={styles.name}>
          {buildingSelectionOnly ? (selectedBuilding?.Name ?? 'Select a building')
            : (selectedFloor?.Name ?? 'Select a floor')}
        </Text>
        <FontAwesome
          name='angle-down'
          color={Colors.light.hereworksBlue500}
          size={24}
          style={floorDropdownVisible ? Style.inverted : Style.upright}
        />
      </Pressable>
      {!buildingSelectionOnly && floorDropdownVisible && <RegionDropdown
        regions={floors}
        selectedRegion={selectedFloor}
        setSelectedRegion={(floor: Floor) => {
          setSelectedFloor(floor);
          setSelectedArea(null);
          setSelectedSpace(null);
        }}
      />}
      {buildingSelectionOnly && floorDropdownVisible && <RegionDropdown
        regions={Object.values(buildings ?? {})}
        selectedRegion={(selectedBuilding as unknown as Region)}
        setSelectedRegion={((building: Building) => {
          setSelectedBuilding(building);
          setSelectedFloor(null);
          setSelectedArea(null);
          setSelectedSpace(null);
        }) as unknown as Dispatch<SetStateAction<Region>>}
      />}
      {(selectedFloor?.areas as Area[])?.length > 0 && <View style={styles.areaSection}>
        <View style={{ flexDirection: 'row' }}>
          <View style={styles.connectorContainer}>
            <View style={styles.floorAreaConnector} />
            <View style={styles.connectorSpot} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={styles.sectionHeader}>
              <Text style={styles.label}>{'2. Area'}</Text>
              {selectedArea && <PressableStyled onPress={() => {
                setSelectedArea(null);
                setSelectedSpace(null);
              }}>
                <DeleteIcon24 />
              </PressableStyled>}
            </View>
            <Pressable
              style={styles.button}
              onPress={() => setAreaDropdownVisible(!areaDropdownVisible)}
            >
              <Text style={styles.name}>{selectedArea?.Name ?? 'Select an area'}</Text>
              <FontAwesome
                name='angle-down'
                color={Colors.light.hereworksBlue500}
                size={24}
                style={areaDropdownVisible ? Style.inverted : Style.upright}
              />
            </Pressable>
          </View>
        </View>
        {areaDropdownVisible && <RegionDropdown
          regions={selectedFloor?.areas as Area[]}
          selectedRegion={selectedArea}
          setSelectedRegion={(area: Area) => {
            setSelectedArea(area);
            setSelectedSpace(null);
          }}
          style={styles.areaDropdown}
        />}
        {(selectedArea?.spaces as Space[])?.length > 0 && <View style={styles.spaceSection}>
          <View style={{ flexDirection: 'row' }}>
            <View style={styles.connectorContainer}>
              <View style={styles.floorAreaConnector} />
              <View style={styles.connectorSpot} />
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.sectionHeader}>
                <Text style={styles.label}>{'3. Space'}</Text>
                {selectedSpace && <PressableStyled onPress={() => setSelectedSpace(null)}>
                  <DeleteIcon24 />
                </PressableStyled>}
              </View>
              <Pressable
                style={styles.button}
                onPress={() => setSpaceDropdownVisible(!spaceDropdownVisible)}
              >
                <Text style={styles.name}>{selectedSpace?.Name ?? 'Select a space'}</Text>
                <FontAwesome
                  name='angle-down'
                  color={Colors.light.hereworksBlue500}
                  size={24}
                  style={spaceDropdownVisible ? Style.inverted : Style.upright}
                />
              </Pressable>
            </View>
          </View>
          {spaceDropdownVisible && <RegionDropdown
            regions={(selectedArea?.spaces as Space[])}
            selectedRegion={selectedSpace}
            setSelectedRegion={setSelectedSpace}
            style={styles.areaDropdown}
          />}
        </View>}
      </View>}
    </View>}
    {...{ isVisible, hide, apply }}
  />;
};

const styles = StyleSheet.create({
  container: {
    maxHeight: 600,
  },
  content: {
    marginBottom: 12,
  },
  searchBar: {
    ...Style.row,
    marginTop: 16,
    paddingHorizontal: 9,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue50,
  },
  searchResults: {
    // position: 'absolute',
    width: '100%',
    marginTop: 4,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue50,
    maxHeight: 200,
  },
  searchResultItem: {
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  listHeader: {
    ...Style.row,
    backgroundColor: Colors.light.hereworksWhite400,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  listHeaderText: {
    color: Colors.light.hereworksBlack400,
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    textTransform: 'uppercase',
  },
  searchIcon: {
    transform: [{ scale: 0.75 }],
  },
  searchText: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
    marginHorizontal: 8,
  },
  label: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  name: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  address: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  button: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    minHeight: 40,
    marginTop: 4,
  },
  areaSection: {
    marginStart: 12,
  },
  areaDropdown: {
    marginStart: 19,
  },
  spaceSection: {
    marginStart: 32,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1.5,
    borderBottomWidth: 1.5,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  sectionHeader: {
    ...Style.row,
    marginTop: 16,
  },
});

export default LocationSelectionModal;
